

import { useEpisode } from '@/contexts/EpisodeContext'
import { useShow } from '@/contexts/ShowContext'
import { apiFetch } from '@/lib/api'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import Loader from '@/components/loader'
import StoryboardCanvas from '@/components/storyboard/StoryboardCanvas'
import StoryboardTimeline from '@/components/storyboard/StoryboardTimeline'

export default function StoryboardPage() {
    const { show } = useShow()
    const { episode, fetchEpisode } = useEpisode()
    const { segmentId } = useParams()
    const [searchParams] = useSearchParams()
    const highlightCommentId = searchParams.get('hc')

    // --- State ---
    const [storyboard, setStoryboard] = useState(null)
    const [loading, setLoading] = useState(true)
    const [selectedElementIndex, setSelectedElementIndex] = useState(null)

    // Active script element ID (from timeline playhead position)
    const [activeScriptId, setActiveScriptId] = useState(null)

    // Counter to tell StoryboardCanvas to deselect its items
    const [deselectItemsTrigger, setDeselectItemsTrigger] = useState(0)
    const triggerDeselectItems = useCallback(() => {
        setDeselectItemsTrigger(prev => prev + 1)
    }, [])

    // Save timeout ref for debounced saves
    const saveTimeoutRef = useRef(null)

    // --- Derived data ---
    const segment = useMemo(() => {
        return episode?.segments?.find(s => s._id === segmentId)
    }, [episode, segmentId])

    // --- API ---
    const fetchStoryboard = useCallback(async () => {
        if (!show?._id || !episode?._id || !segmentId) return
        try {
            const res = await apiFetch(`/api/shows/${show._id}/episodes/${episode._id}/segments/${segmentId}/storyboard`)
            if (res.ok) {
                const data = await res.json()
                setStoryboard(data)
            }
        } catch (err) {
            console.error('Error fetching storyboard:', err)
        }
    }, [show?._id, episode?._id, segmentId])

    const saveStoryboard = useCallback(async (sb) => {
        if (!sb?._id) return
        try {
            await apiFetch(`/api/shows/${show._id}/episodes/${episode._id}/segments/${segmentId}/storyboard`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ elements: sb.elements })
            })
        } catch (err) {
            console.error('Error saving storyboard:', err)
        }
    }, [show?._id, episode?._id, segmentId])

    const debouncedSaveStoryboard = useCallback((sb) => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        saveTimeoutRef.current = setTimeout(() => saveStoryboard(sb), 1500)
    }, [saveStoryboard])

    const updateStoryboard = useCallback((updater) => {
        setStoryboard(prev => {
            const next = typeof updater === 'function' ? updater(prev) : updater
            if (next !== prev) {
                debouncedSaveStoryboard(next)
            }
            return next
        })
    }, [debouncedSaveStoryboard])

    // --- Init ---
    useEffect(() => {
        const load = async () => {
            setLoading(true)
            await Promise.all([fetchStoryboard(), fetchEpisode()])
            setLoading(false)
        }
        load()
    }, [fetchStoryboard])

    // Auto-select the canvas at startTime 0 on first load
    useEffect(() => {
        if (loading || !storyboard?.elements?.length) return
        if (selectedElementIndex !== null) return
        const idx = storyboard.elements.findIndex(el => el.startTime === 0)
        if (idx !== -1) setSelectedElementIndex(idx)
    }, [loading, storyboard])

    // Handle ?hc= comment navigation — find the comment's element and select it
    useEffect(() => {
        if (!highlightCommentId || loading || !storyboard?.elements?.length || !show?._id) return

        const navigateToComment = async () => {
            try {
                const res = await apiFetch(`/api/shows/${show._id}/comments/${highlightCommentId}`)
                if (!res.ok) return
                const comment = await res.json()
                const elementId = typeof comment.element === 'object' ? comment.element.toString() : comment.element
                const idx = storyboard.elements.findIndex(e => e._id === elementId)
                if (idx !== -1) setSelectedElementIndex(idx)
            } catch (err) {
                console.error('Error navigating to comment:', err)
            }
        }
        navigateToComment()
    }, [highlightCommentId, loading, storyboard, show?._id])

    // --- Cleanup ---
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
        }
    }, [])

    if (loading) return <Loader />

    // ==============================
    // RENDER
    // ==============================
    return (
        <div className='flex flex-col h-full select-none'>
            <StoryboardCanvas
                storyboard={storyboard}
                updateStoryboard={updateStoryboard}
                selectedElementIndex={selectedElementIndex}
                setSelectedElementIndex={setSelectedElementIndex}
                loading={loading}
                deselectItemsTrigger={deselectItemsTrigger}
                highlightCommentId={highlightCommentId}
                activeScriptId={activeScriptId}
            />

            <StoryboardTimeline
                storyboard={storyboard}
                updateStoryboard={updateStoryboard}
                selectedElementIndex={selectedElementIndex}
                setSelectedElementIndex={setSelectedElementIndex}
                triggerDeselectItems={triggerDeselectItems}
                segment={segment}
                onActiveScriptChange={setActiveScriptId}
            />
        </div>
    )
}
